'use client';

import { c as _c } from "react/compiler-runtime";
import { HorizontalOverflowCarouselItem } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Product } from '@libs/analytics';
import { createItemsViewEvent, getSearchStats, TrackingParams, trackContentfulClick, trackProductTileItemClick } from '@libs/analytics';
import { resolveFrefListAttribution } from '@libs/common-next';
import { mapYmalProductsToAnalyticsProducts, mapYmalProductToAnalyticsCategories } from '../lib';
import type { YmalProductTrackerProps } from './ymalProductsCarousel.types';
const PROMO_TITLE = 'You may also like';
const PROMO_LIST_ID = 'product carousel';
const PROMO_VARIANT = 'traditional';
const PROMO_TYPE = 'product carousel - traditional';

// Analytics wants three category levels for the page a shopper clicked from.
// The cart isn't a category page, so all three go out as null.
const CART_PAGE_CATEGORIES = [null, null, null];
export function YmalProductTracker(t0) {
  const $ = _c(19);
  const {
    children,
    position,
    product,
    frefAttribution,
    onView
  } = t0;
  const pathname = usePathname();
  const t1 = !onView;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      threshold: 0.2,
      skip: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    ref,
    inView
  } = useInView(t2);
  let t3;
  let t4;
  if ($[2] !== inView || $[3] !== onView) {
    t3 = () => {
      if (inView) {
        onView?.();
      }
    };
    t4 = [inView, onView];
    $[2] = inView;
    $[3] = onView;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== frefAttribution || $[7] !== pathname || $[8] !== position || $[9] !== product) {
    t5 = () => {
      if (frefAttribution) {
        globalThis.sessionStorage.setItem("pdpFindingMethod", `${frefAttribution.listId}_${frefAttribution.listName}_${position}`);
      } else {
        const referrer = TrackingParams.PRODUCT_CAROUSEL_REFERER;
        if (referrer) {
          globalThis.sessionStorage.setItem("pdpFindingMethod", String(referrer));
        }
      }
      const analyticsProduct = mapYmalProductsToAnalyticsProducts([product])[0];
      const listAttribution = frefAttribution ? resolveFrefListAttribution(frefAttribution) : undefined;
      const {
        listName,
        listId,
        categories,
        searchTerm,
        listPosition,
        pdpType
      } = listAttribution ? {
        listName: listAttribution.item_list_name,
        listId: listAttribution.item_list_id,
        categories: CART_PAGE_CATEGORIES,
        searchTerm: null,
        listPosition: position,
        pdpType: null
      } : {
        listName: PROMO_TITLE,
        listId: PROMO_LIST_ID,
        categories: mapYmalProductToAnalyticsCategories(product),
        searchTerm: getSearchStats().searchTerm,
        listPosition: undefined,
        pdpType: undefined
      };
      trackProductTileItemClick({
        product: analyticsProduct as unknown as Product,
        ...(listPosition !== undefined && {
          listPosition
        }),
        pdpType,
        ...createItemsViewEvent([analyticsProduct], listName, listId, categories, searchTerm)
      });
      trackContentfulClick({
        position,
        linkText: product.name,
        linkUrl: product.url,
        promoTitle: PROMO_TITLE,
        promoVariant: PROMO_VARIANT,
        promoDesc: "you-may-also-like-products-carousel",
        promoType: PROMO_TYPE,
        promoOrigin: pathname
      });
    };
    $[6] = frefAttribution;
    $[7] = pathname;
    $[8] = position;
    $[9] = product;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleClick = t5;
  let t6;
  if ($[11] !== children || $[12] !== onView || $[13] !== ref) {
    t6 = onView ? <div ref={ref}>{children}</div> : children;
    $[11] = children;
    $[12] = onView;
    $[13] = ref;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== handleClick || $[16] !== product.name || $[17] !== t6) {
    t7 = <HorizontalOverflowCarouselItem data-testid={product.name} onClick={handleClick}>{t6}</HorizontalOverflowCarouselItem>;
    $[15] = handleClick;
    $[16] = product.name;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}